import { type AuditLogQuery } from "@commerce-os/contracts";
import { addDays, zonedDayStart } from "@commerce-os/utils";

/** The audit log's filters, kept in the URL so a filtered view can be shared or reloaded. */
export interface AuditSearch {
  entity?: string;
  action?: string;
  /** Calendar days (`2026-10-24`) in the tenant's zone; `to` is inclusive. */
  from?: string;
  to?: string;
}

const day = /^\d{4}-\d{2}-\d{2}$/;

export function auditSearch(search: Record<string, unknown>): AuditSearch {
  const text = (value: unknown) =>
    typeof value === "string" && value.trim() !== "" ? value.trim().slice(0, 128) : undefined;
  const date = (value: unknown) => (typeof value === "string" && day.test(value) ? value : undefined);
  const out: AuditSearch = {};
  const entity = text(search["entity"]);
  const action = text(search["action"]);
  const from = date(search["from"]);
  const to = date(search["to"]);
  if (entity !== undefined) out.entity = entity;
  if (action !== undefined) out.action = action;
  if (from !== undefined) out.from = from;
  if (to !== undefined) out.to = to;
  return out;
}

/** URL filters → `GET /v1/audit-logs` query: days become instants in the zone; `to` covers the whole day. */
export function auditQuery(search: AuditSearch, timeZone: string): Omit<AuditLogQuery, "limit"> {
  return {
    ...(search.entity === undefined ? {} : { entity: search.entity }),
    ...(search.action === undefined ? {} : { action: search.action }),
    ...(search.from === undefined ? {} : { from: zonedDayStart(search.from, timeZone) }),
    ...(search.to === undefined ? {} : { to: zonedDayStart(addDays(search.to, 1), timeZone) }),
  };
}
